import type { KYDEventRefundExecution } from "../interfaces/KYDEvent";

export const REFUND_EVENT_WAIT_STATE_NAME = "WaitBeforeRefund";
export const REFUND_EVENT_CANCEL_WAIT_SECONDS = 60 * 60;

export const refundExecutionIsInWait = (
  event?: {
    type?: string;
    stateEnteredEventDetails?: { name?: string };
  } | null
): boolean =>
  event?.type === "WaitStateEntered" &&
  event.stateEnteredEventDetails?.name === REFUND_EVENT_WAIT_STATE_NAME;

export const refundExecutionCanAbort = (
  execution?: {
    execution_arn?: string;
    wait_until?: string;
    aborted_at?: string;
    claimed_at?: string;
  } | null,
  now = Date.now()
): boolean => {
  if (
    !execution?.execution_arn ||
    execution.aborted_at ||
    execution.claimed_at ||
    !execution.wait_until
  ) {
    return false;
  }
  const waitUntil = Date.parse(execution.wait_until);
  return Number.isFinite(waitUntil) && waitUntil > now;
};

/**
 * Cancellation jobs must be the saved ARN so an untracked wait cannot refund fans.
 * Standalone refunds are not part of that race.
 */
export const refundExecutionClaimResult = ({
  execution_arn,
  saved_execution_arn,
  claimed_at,
  aborted_at,
  cancellation,
}: {
  execution_arn: string;
  saved_execution_arn?: string;
  claimed_at?: string;
  aborted_at?: string;
  cancellation: boolean;
}): "claimed" | "lost" => {
  const tracked = saved_execution_arn === execution_arn;
  if (cancellation && !tracked) {
    return "lost";
  }
  if (!tracked) {
    return "claimed";
  }
  if (claimed_at && !aborted_at) {
    return "claimed";
  }
  return "lost";
};

export type CancelEventRefundStart = "transition" | "retry" | "in_progress" | "stopped";

/** A canceled event with no saved refund job can start one again. */
export const cancelEventRefundStart = (root: {
  status?: string;
  refund_execution_arn?: string;
  refund_execution_aborted_at?: string;
  refund_execution_claimed_at?: string;
}): CancelEventRefundStart => {
  if (root.status !== "CANCELED") {
    return "transition";
  }
  if (root.refund_execution_aborted_at) {
    return "stopped";
  }
  if (root.refund_execution_arn || root.refund_execution_claimed_at) {
    return "in_progress";
  }
  return "retry";
};

export const refundExecutionForAdminEvent = (
  metadata?: {
    refund_execution_arn?: string;
    refund_wait_until?: string;
    refund_execution_aborted_at?: string;
    refund_execution_claimed_at?: string;
  },
  now = Date.now()
): KYDEventRefundExecution | undefined => {
  if (!metadata?.refund_execution_arn || !metadata.refund_wait_until) {
    return undefined;
  }

  const aborted_at = metadata.refund_execution_aborted_at;
  const claimed_at = metadata.refund_execution_claimed_at;
  return {
    execution_arn: metadata.refund_execution_arn,
    wait_until: metadata.refund_wait_until,
    aborted_at,
    can_abort: refundExecutionCanAbort(
      {
        execution_arn: metadata.refund_execution_arn,
        wait_until: metadata.refund_wait_until,
        aborted_at,
        claimed_at,
      },
      now
    ),
  };
};
